import React, {useEffect, useMemo} from "react";
import {Button, Chat, DefaultModal, GradientButton, Page, Text, UserInfo, UserInfoStat, UserInfoWrapper} from "@housebets/housebets-x-package";
import {useGTMEvents} from "app/hooks/useGTMEvents";
import {IChatModalsProps} from "./types";
import {HousebetsLogoIcon} from "@housebets/housebets-x-package/lib/components/icons";
import {useAppSelector} from "hooks/useAppSelector";
import {useIntl} from "react-intl";
import {FormattedMessage} from "react-intl";

export const ChatModals: React.FC<IChatModalsProps> = ({
    isRulesOpen,
    setRulesOpen,
    isPermanentBanInfoOpen,
    setPermanentBanInfoOpen,
    isTempBanInfoOpen,
    setTempBanInfoOpen,
    userInfoModalData,
    setUserInfoModalData,
    usersData = {},
    handleBlockUser,
    handleUnBlockUser,
    blacklistUsers,
}) => {
    const {$t} = useIntl();

    const {modal} = useGTMEvents();

    const platformConfig = useAppSelector((state) => state.config.platformConfig);
    const currentUser =
        userInfoModalData && userInfoModalData.userData && userInfoModalData.userData.id !== undefined ? usersData[userInfoModalData.userData.id] : undefined;

    const loggedTextData = useMemo(() => {
        const vipList = platformConfig?.vipList || [];
        if (currentUser && currentUser.level) {
            const currentLevel = vipList?.find((v) => +v.level === +currentUser?.level);
            const levelTitle = currentLevel?.name;
            const matchName = levelTitle?.match(/^([^\d]+)\s*(\d+)$/);

            return {
                levelTitle: levelTitle || "",
                levelTitleLabel: matchName?.length && matchName[2] !== undefined ? $t({id: String(matchName[1]?.trim() || "")}) + " " + matchName[2] : "",
            };
        } else {
            return {
                levelTitle: "",
            };
        }
    }, [platformConfig, currentUser]);

    return (
        <>
            <DefaultModal
                isOpen={isPermanentBanInfoOpen}
                onClose={() => setPermanentBanInfoOpen(false)}
                title="Permanently banned"
                afterOpenModal={() => {
                    modal.show("permanent_ban_info");
                }}
                onAfterClose={() => {
                    modal.close("permanent_ban_info");
                }}>
                <Chat.Components.ChatInfo>
                    <p>
                        <FormattedMessage id="ChatModalSpamText1" />
                    </p>
                    <ul>
                        <li>
                            <strong>
                                <FormattedMessage id="ChatModalSpamText1.title1" />
                            </strong>{" "}
                            <FormattedMessage id="ChatModalSpamText1.desc1" />
                        </li>
                        <li>
                            <strong>
                                <FormattedMessage id="ChatModalSpamText1.title2" />
                            </strong>{" "}
                            <FormattedMessage id="ChatModalSpamText1.desc2" />
                        </li>
                        <li>
                            <strong>
                                <FormattedMessage id="ChatModalSpamText1.title3" />
                            </strong>{" "}
                            <FormattedMessage id="ChatModalSpamText1.desc3" />
                        </li>
                    </ul>
                    <Chat.Components.ChatInfoWarning>
                        <FormattedMessage
                            id="ChatModalSpamText1.desc4"
                            values={{
                                strong: (chunks) => <strong>{chunks}</strong>,
                            }}
                        />
                    </Chat.Components.ChatInfoWarning>
                    <GradientButton kind="fill" text="Got it" onClick={() => setPermanentBanInfoOpen(false)} />
                </Chat.Components.ChatInfo>
            </DefaultModal>
            <DefaultModal
                isOpen={isRulesOpen}
                onClose={() => setRulesOpen(false)}
                title={$t({id: "ChatRules"})}
                afterOpenModal={() => {
                    modal.show("chat_rules_info");
                }}
                onAfterClose={() => {
                    modal.close("chat_rules_info");
                }}>
                <Chat.Components.ChatInfo>
                    <Page.Wrapper>
                        <HousebetsLogoIcon />
                    </Page.Wrapper>

                    <Text v2>
                        <ul>
                            <li>
                                <FormattedMessage id="ChatRules.text1" />
                            </li>

                            <li>
                                <FormattedMessage id="ChatRules.text2" />
                            </li>
                            <li>
                                <FormattedMessage id="ChatRules.text3" />
                            </li>
                            <li>
                                <FormattedMessage id="ChatRules.text4" />
                            </li>
                            <li>
                                <FormattedMessage id="ChatRules.text5" />
                            </li>
                            <li>
                                <FormattedMessage id="ChatRules.text6" />
                            </li>
                        </ul>
                    </Text>
                </Chat.Components.ChatInfo>
            </DefaultModal>
            <DefaultModal
                isOpen={isTempBanInfoOpen}
                onClose={() => setTempBanInfoOpen(false)}
                title="Temporarily banned"
                afterOpenModal={() => {
                    modal.show("chat_temporary_ban_info");
                }}
                onAfterClose={() => {
                    modal.close("chat_temporary_ban_info");
                }}>
                <Chat.Components.ChatInfo>
                    <p>
                        {" "}
                        <FormattedMessage id="ChatModal.warningText" />
                    </p>
                    <ul>
                        <li>
                            <strong>
                                <FormattedMessage id="ChatModal.rule1.title" />
                            </strong>{" "}
                            <FormattedMessage id="ChatModal.rule1.desc" />
                        </li>
                        <li>
                            <strong>
                                <FormattedMessage id="ChatModal.rule2.title" />
                            </strong>{" "}
                            <FormattedMessage id="ChatModal.rule2.desc" />
                        </li>
                        <li>
                            <strong>
                                <FormattedMessage id="ChatModal.rule3.title" />
                            </strong>{" "}
                            <FormattedMessage id="ChatModal.rule3.desc" />
                        </li>
                    </ul>
                    <Chat.Components.ChatInfoWarning>
                        <FormattedMessage
                            id="ChatModal.warningFooter"
                            values={{
                                strong: (chunks) => <strong>{chunks}</strong>,
                            }}
                        />
                    </Chat.Components.ChatInfoWarning>
                    <GradientButton kind="fill" text="Got it" onClick={() => setTempBanInfoOpen(false)} />
                </Chat.Components.ChatInfo>
            </DefaultModal>
            <DefaultModal
                isOpen={userInfoModalData.isOpen}
                onClose={() =>
                    setUserInfoModalData({
                        ...userInfoModalData,
                        isOpen: false,
                    })
                }
                title={$t({id: "UserInfo"})}
                afterOpenModal={() => {
                    modal.show("chat_user_info");
                }}
                onAfterClose={() => {
                    modal.close("chat_user_info");
                }}>
                {userInfoModalData.isOpen && (
                    <UserInfoWrapper
                        top={
                            userInfoModalData.userData && (
                                <UserInfo
                                    {...userInfoModalData.userData}
                                    rank={loggedTextData.levelTitle}
                                    rankLabel={loggedTextData.levelTitleLabel}
                                    showRank
                                    configurable={false}
                                    onEdit={() => {
                                        // empty
                                    }}
                                    onSubmit={() => {
                                        // empty
                                    }}
                                    text={{
                                        labelCopy: $t({id: "ShareProfile"}),
                                    }}
                                />
                            )
                        }
                        // bottom={null}
                        bottom={
                            userInfoModalData.userStatistics && (
                                <UserInfoStat
                                    data={[
                                        {
                                            icon: {
                                                customIcon: "",
                                                iconName: "GameIcon",
                                            },
                                            title: $t({id: "totalWins"}),
                                            value:
                                                userInfoModalData.userData?.id !== undefined && usersData[userInfoModalData.userData.id] !== undefined
                                                    ? usersData[userInfoModalData.userData.id]!.total_wins.toString()
                                                    : "...",

                                            hidden: false,
                                        },
                                        {
                                            icon: {
                                                customIcon: "",
                                                iconName: "CoinsIcon",
                                            },
                                            title: $t({id: "totalWagered"}),
                                            value:
                                                userInfoModalData.userData?.id !== undefined && usersData[userInfoModalData.userData.id] !== undefined
                                                    ? Number(usersData[userInfoModalData.userData.id]!.total_wagered)?.toFixed(2)?.toString()
                                                    : "...",
                                            hidden: false,
                                        },
                                        {
                                            icon: {
                                                customIcon: "",
                                                iconName: "MoneyBagIcon",
                                            },
                                            title: $t({id: "totalBets"}),
                                            value:
                                                userInfoModalData.userData?.id !== undefined && usersData[userInfoModalData.userData.id] !== undefined
                                                    ? usersData[userInfoModalData.userData.id]!.total_bets.toString()
                                                    : "...",
                                            hidden: false,
                                        },
                                    ]}
                                />
                            )
                        }
                        buttons={
                            <>
                                {/* <GradientButton
                                    text="Tip User"
                                    kind="fill"
                                    onClick={() => {
                                        // empty
                                    }}
                                    fullWidth
                                /> */}
                                {blacklistUsers.some((user) => String(user.user_id) === String(userInfoModalData?.userData?.id || "")) ? (
                                    <Button
                                        text={$t({id: "Unmute"})}
                                        size="medium-3"
                                        themeKind="dark-blue"
                                        onClick={() => {
                                            handleUnBlockUser({
                                                user_id: +(userInfoModalData?.userData?.id || ""),
                                                nick: "",
                                                avatar: "",
                                                level: 0,
                                            });
                                        }}
                                    />
                                ) : (
                                    <Button
                                        text={$t({id: "Mute"})}
                                        size="medium-3"
                                        themeKind="dark-blue"
                                        onClick={() => {
                                            handleBlockUser({
                                                user_id: +(userInfoModalData?.userData?.id || ""),
                                                nick: "",
                                                avatar: "",
                                                level: 0,
                                            });
                                        }}
                                    />
                                )}
                            </>
                        }
                    />
                )}
            </DefaultModal>
        </>
    );
};

ChatModals.displayName = "ChatModals";
